// ── emailTypo.js ──────────────────────────────────────────────────────────────
// A mistyped email domain is a silent, total loss: the magic link goes nowhere,
// the account exists, and the person can never get in. It doesn't even look like
// a failure in the funnel — it looks like someone who signed up and never came
// back. Three real accounts died this way (gmail.comm, egmail.com, gmail.comcp7,
// all with last_active=null), and input type="email" can't catch any of them
// because they're syntactically valid.
//
// This is a SUGGESTION, never a block: we can't know that "gmail.co" is wrong
// (it's a real domain), so the user always keeps the address they typed.

// Domains worth correcting toward — the ones our signups actually use, plus the
// big providers. A short list on purpose: every entry is a chance to "correct" a
// legitimate address into the wrong one, so obscure domains stay off it.
const KNOWN = [
  'gmail.com', 'googlemail.com', 'yahoo.com', 'ymail.com', 'hotmail.com',
  'outlook.com', 'live.com', 'msn.com', 'icloud.com', 'me.com', 'mac.com',
  'aol.com', 'proton.me', 'protonmail.com', 'pm.me', 'tuta.io', 'tutamail.com',
  'comcast.net', 'charter.net', 'verizon.net', 'att.net', 'sbcglobal.net',
  'cox.net', 'bellsouth.net', 'mail.com', 'gmx.com', 'zoho.com', 'yandex.com',
]

/** Levenshtein distance, capped — we only ever care about "is this ≤2 edits away". */
function distance(a, b) {
  if (a === b) return 0
  if (Math.abs(a.length - b.length) > 2) return 99
  let prev = Array.from({ length: b.length + 1 }, (_, i) => i)
  for (let i = 1; i <= a.length; i++) {
    const cur = [i]
    for (let j = 1; j <= b.length; j++) {
      cur[j] = Math.min(
        prev[j] + 1,
        cur[j - 1] + 1,
        prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1),
      )
    }
    prev = cur
  }
  return prev[b.length]
}

/**
 * Suggest a corrected address, or null when the address looks fine (or when we
 * aren't confident enough to guess). Only the DOMAIN is ever corrected — the
 * local part is the user's name and we have no business rewriting it.
 */
export function suggestEmailFix(input) {
  const addr = String(input || '').trim().toLowerCase()
  const at = addr.lastIndexOf('@')
  if (at <= 0 || at === addr.length - 1) return null      // not a complete address yet
  const local  = addr.slice(0, at)
  const domain = addr.slice(at + 1)
  if (!domain.includes('.')) return null                   // still typing
  if (KNOWN.includes(domain)) return null                  // already correct

  for (const k of KNOWN) {
    // "gmail.comcp7" / "gmail.com." — a known domain with trailing junk. Catches
    // the fat-finger-after-the-TLD case that edit distance scores too far away.
    if (domain.length > k.length && domain.startsWith(k)) return `${local}@${k}`
  }
  // Closest known domain within 2 edits wins. Requires a reasonably long domain
  // so short ones ("a.co") don't get pulled toward unrelated entries.
  let best = null, bestD = 3
  for (const k of KNOWN) {
    if (k.length < 5) continue
    const d = distance(domain, k)
    if (d < bestD) { bestD = d; best = k }
  }
  return best ? `${local}@${best}` : null
}
